// =============================================================================
// ReimbursementsScreen — /ap/reimbursements   (G4, part one)
// =============================================================================
// THE QUESTION THIS SCREEN ANSWERS
// -------------------------------
// "What do we owe the crew right now, and who?"
//
// Until now that answer was scattered: a reimbursement is just an ap_invoice
// with is_reimbursement = true, sitting in the same queue as every supplier
// bill. So the only way to find out what Jairyn was owed was to read the whole
// AP list and pick his rows out by eye. That is how a $40 fuel receipt from
// three weeks ago goes unpaid.
//
// WHY GROUPED BY PERSON AND NOT BY WEEK
// -------------------------------------
// The end goal (G4 part two) is one bill per person covering several receipts,
// instead of a bill per receipt. James described it as a weekly rhythm, but
// "weekly" is how often he'd do it, not what defines a bundle - a receipt from
// last fortnight that never got paid still belongs on the next bill. So the
// grouping is by PERSON and shows everything outstanding, with the age of the
// oldest claim called out. Run it weekly and you get weekly bills.
//
// THE PUSH
// --------
// "Create one bill in Xero" posts a single DRAFT bill against the person's Xero
// contact with one line per receipt, each keeping its own account code and GST
// treatment from coding. Draft, not approved - the accounts person still opens
// Xero and approves it, exactly like the single push.
//
// Reimbursements are deliberately NOT in the Push to Xero queue any more. Two
// buttons that both send the same receipt to Xero is how somebody gets paid
// twice, and that is not a recoverable mistake.
//
// 23 Sep 2026 - the worker now reads the bill back from Xero after the post
// (total, lines, contact) and says here whether Xero took it as sent. A "no"
// is shown in red and the bill stays flagged on Bills to pay.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Users, AlertTriangle, ChevronRight, ChevronDown,
  Receipt, CalendarClock, Link2Off, Upload, CheckCircle2,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

// -----------------------------------------------------------------------------
// What counts as outstanding
// -----------------------------------------------------------------------------
// Anything flagged as a reimbursement that has NOT yet been handed to Xero as a
// bill. xero_bill_id is the honest test: it is only set once Xero has actually
// accepted it, so a push that failed halfway still shows here rather than
// quietly disappearing.
//
// void / disputed are excluded - they are decisions already made. 'paid' is
// excluded too: a reimbursement marked paid outside Xero is settled, and
// leaving it here would have people paid twice.
const OPEN_STATUSES = ["captured", "pending_review", "approved", "entered_in_xero"] as const;

interface ClaimRow {
  ap_invoice_id: string;
  reimburse_employee_id: string | null;
  supplier_name: string | null;
  description: string | null;
  invoice_date: string | null;
  captured_at: string | null;
  amount_inc_gst: number | null;
  amount_ex_gst: number | null;
  gst_amount: number | null;
  project_id: string | null;
  budget_code: string | null;
  xero_account_code: string | null;
  status: string;
  _proj?: string | null;
}

interface PersonGroup {
  employee_id: string;
  name: string;
  xero_contact_id: string | null;
  xero_contact_name: string | null;
  claims: ClaimRow[];
  total: number;
  oldestDays: number | null;
}

// A claim's age runs from the date on the receipt where there is one, and from
// capture otherwise. Someone who sat on a receipt for a fortnight before
// snapping it hasn't been out of pocket any less long.
function claimDate(c: ClaimRow): string | null {
  return c.invoice_date ?? (c.captured_at ? c.captured_at.slice(0, 10) : null);
}
function ageDays(d: string | null): number | null {
  if (!d) return null;
  return Math.floor((Date.now() - new Date(d + "T00:00:00").getTime()) / 86400000);
}
function ageTone(n: number | null): { cls: string; tone: "ok" | "warning" | "overdue" } {
  if (n == null)  return { cls: "text-text-muted", tone: "ok" };
  if (n >= 30)    return { cls: "text-danger",     tone: "overdue" };
  if (n >= 14)    return { cls: "text-warn",       tone: "warning" };
  return { cls: "text-text-muted", tone: "ok" };
}

export default function ReimbursementsScreen() {
  const user = useRequireAuth();
  const canSee = hasRole(user, "manager");
  const [openPerson, setOpenPerson] = useState<string | null>(null);
  // Everything on a person is in the bill unless it is explicitly ticked OUT.
  // Tracking exclusions rather than inclusions means the list never has to be
  // re-synced with the query when it refetches.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [busyPerson, setBusyPerson] = useState<string | null>(null);
  const [pushError, setPushError]   = useState<string | null>(null);
  const [pushed, setPushed]         = useState<{ name: string; number: string | null; lines: number; total: number; attached: number;
                                                  // 23 Sep 2026 - Xero's own answer, read back after the post
                                                  xeroTotal: number | null; readbackOk: boolean; readbackDetail: string[] | null } | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["reimbursements_pending"],
    enabled: !!user && canSee,
    queryFn: async (): Promise<PersonGroup[]> => {
      const { data: rows, error: e1 } = await supabase
        .from("ap_invoices")
        .select("ap_invoice_id, reimburse_employee_id, supplier_name, description, invoice_date, captured_at, amount_inc_gst, amount_ex_gst, gst_amount, project_id, budget_code, xero_account_code, status")
        .eq("is_reimbursement", true)
        .is("xero_bill_id", null)
        .in("status", OPEN_STATUSES)
        .order("invoice_date", { ascending: true, nullsFirst: false })
        .limit(500);
      if (e1) throw e1;

      const claims = (rows ?? []) as ClaimRow[];

      // Project codes, so a line reads "SITE-1042" not a UUID.
      const pids = Array.from(new Set(claims.map(c => c.project_id).filter(Boolean))) as string[];
      if (pids.length) {
        const { data: ps } = await supabase
          .from("projects").select("project_id, project_code").in("project_id", pids);
        const byP = new Map((ps ?? []).map((p: any) => [p.project_id, p.project_code]));
        claims.forEach(c => { c._proj = c.project_id ? byP.get(c.project_id) ?? null : null; });
      }

      // Names AND the Xero contact link (G3b) in one read - "who is owed" and
      // "can they actually be billed" are the same question here.
      const eids = Array.from(new Set(claims.map(c => c.reimburse_employee_id).filter(Boolean))) as string[];
      const byE = new Map<string, any>();
      if (eids.length) {
        const { data: es } = await supabase
          .from("employees")
          .select("employee_id, first_name, last_name, xero_contact_id, xero_contact_name")
          .in("employee_id", eids);
        (es ?? []).forEach((e: any) => byE.set(e.employee_id, e));
      }

      const groups = new Map<string, PersonGroup>();
      for (const c of claims) {
        // A reimbursement with nobody attached is a real state - it happens when
        // a receipt is declared personal but the coder never picked the person.
        // Bucketing it under a sentinel keeps it visible instead of dropping it.
        const key = c.reimburse_employee_id ?? "__unassigned__";
        const emp = c.reimburse_employee_id ? byE.get(c.reimburse_employee_id) : null;
        if (!groups.has(key)) {
          groups.set(key, {
            employee_id: key,
            name: emp
              ? [emp.first_name, emp.last_name].filter(Boolean).join(" ")
              : (c.reimburse_employee_id ? "Unknown employee" : "Not assigned to anyone"),
            xero_contact_id:   emp?.xero_contact_id   ?? null,
            xero_contact_name: emp?.xero_contact_name ?? null,
            claims: [], total: 0, oldestDays: null,
          });
        }
        const g = groups.get(key)!;
        g.claims.push(c);
        g.total += Number(c.amount_inc_gst ?? 0);
        const a = ageDays(claimDate(c));
        if (a != null && (g.oldestDays == null || a > g.oldestDays)) g.oldestDays = a;
      }

      // Oldest debt first. Being owed money the longest is the thing that should
      // pull someone to the top of the list, not being owed the most.
      return Array.from(groups.values())
        .sort((a, b) => (b.oldestDays ?? -1) - (a.oldestDays ?? -1));
    },
  });

  const groups = data ?? [];

  function toggle(id: string) {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function pushBundle(g: PersonGroup) {
    const ids = g.claims.map(c => c.ap_invoice_id).filter(id => !excluded.has(id));
    if (!ids.length) return;
    setBusyPerson(g.employee_id);
    setPushError(null);
    setPushed(null);
    try {
      const res = await apiFetch("/ap/xero/reimbursement-bundle", {
        method: "POST",
        body: JSON.stringify({ employee_id: g.employee_id, ap_invoice_ids: ids }),
      });
      const j = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        // Xero's own validation messages are the useful part when it refuses.
        throw new Error((j?.messages?.join(" ") || j?.detail || j?.error) ?? `Push failed (${res.status})`);
      }
      setPushed({
        name: j.employee_name ?? g.name,
        number: j.xero_number ?? null,
        lines: j.lines_sent ?? ids.length,
        total: Number(j.total_inc_gst ?? 0),
        attached: Number(j.attached ?? 0),
        xeroTotal: j.xero_total != null ? Number(j.xero_total) : null,
        readbackOk: j.readback_ok !== false,
        readbackDetail: Array.isArray(j.readback_detail) ? j.readback_detail.map(String) : null,
      });
      setOpenPerson(null);
      await refetch();
    } catch (e: any) {
      setPushError(e?.message ?? String(e));
    } finally {
      setBusyPerson(null);
    }
  }

  const kpis = useMemo(() => {
    const total   = groups.reduce((a, g) => a + g.total, 0);
    const claims  = groups.reduce((a, g) => a + g.claims.length, 0);
    const oldest  = groups.reduce<number | null>(
      (a, g) => (g.oldestDays != null && (a == null || g.oldestDays > a)) ? g.oldestDays : a, null);
    const blocked = groups.filter(g => !g.xero_contact_id && g.employee_id !== "__unassigned__");
    return { total, claims, people: groups.length, oldest, blocked };
  }, [groups]);

  if (!user) return null;

  if (!canSee) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <EmptyState icon={AlertTriangle} title="Managers only"
            description="Reimbursements are visible to managers and directors." />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <Link to="/ap" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> AP / Spend
      </Link>

      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">Reimbursements</h1>
        <div className="text-text-muted text-[13px] mt-1">
          Out-of-pocket spend the company still owes its people. Grouped by person, so one
          bill can cover the lot instead of one bill per receipt.
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Owed" value={fmtMoney(kpis.total)} sub="incl. GST" />
        <KPI label="People" value={String(kpis.people)} sub="with something outstanding" />
        <KPI label="Receipts" value={String(kpis.claims)} sub="across everyone" />
        <KPI label="Oldest claim"
             value={kpis.oldest == null ? "—" : `${kpis.oldest}d`}
             sub="since the receipt date" />
      </div>

      {/* Anyone not linked to a Xero contact cannot be billed. Saying so here,
          with a way straight to the fix, beats finding out at push time. */}
      {kpis.blocked.length > 0 && (
        <Card className="mb-4">
          <div className="p-4 flex items-start gap-2.5">
            <Link2Off size={16} className="text-warn mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[13px] text-text">
                {kpis.blocked.length === 1
                  ? "One person isn't linked to a Xero contact yet."
                  : `${kpis.blocked.length} people aren't linked to a Xero contact yet.`}
              </div>
              <div className="text-[12px] text-text-dim mt-0.5">
                A bill has to be raised against a Xero contact, so these can't be pushed until
                the link is made. Open the person and use <span className="text-text">Find in Xero</span>.
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {kpis.blocked.map(g => (
                  <Link key={g.employee_id} to={`/people/${g.employee_id}`}
                        className="text-[12px] px-2 py-0.5 rounded border border-line bg-ink-raised text-accent hover:border-accent">
                    {g.name} →
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {pushed && (
        <Card className="mb-4">
          <div className="p-4 flex items-start gap-2.5">
            <CheckCircle2 size={16} className="text-ok mt-0.5 flex-shrink-0" />
            <div className="min-w-0 text-[13px] text-text">
              Draft bill created for <span className="font-medium">{pushed.name}</span>
              {pushed.number ? <> — <span className="font-mono">{pushed.number}</span></> : null}:{" "}
              {pushed.lines} {pushed.lines === 1 ? "line" : "lines"}, {fmtMoney(pushed.total)}, {pushed.attached} receipt
              {pushed.attached === 1 ? "" : "s"} attached.
              {pushed.readbackOk ? (
                <div className="text-[12px] text-text-dim mt-0.5">
                  Xero confirms {fmtMoney(pushed.xeroTotal ?? pushed.total)} across {pushed.lines} {pushed.lines === 1 ? "line" : "lines"}.
                  It's a DRAFT — approve it in Xero before it hits the ledger.
                </div>
              ) : (
                // 23 Sep 2026 - the bill is in Xero but not as sent. Say so here
                // and leave the red chip on Bills to pay; don't pretend it landed.
                <div className="text-[12px] text-danger mt-0.5">
                  Xero did not take this bill as sent
                  {pushed.xeroTotal != null ? <> — it holds {fmtMoney(pushed.xeroTotal)}</> : null}.
                  {pushed.readbackDetail?.length ? <> {pushed.readbackDetail.join("; ")}.</> : null}
                  {" "}Open it in Xero before anyone approves or pays it.
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {pushError && (
        <Card className="mb-4">
          <div className="p-4 text-[13px] text-danger">
            Xero wouldn't take it: {pushError}
            <div className="text-[12px] text-text-dim mt-0.5">
              Nothing was sent. Fix the reason above and try again.
            </div>
          </div>
        </Card>
      )}

      {error && (
        <Card className="mb-4">
          <div className="p-4 text-[13px] text-danger">
            Couldn't read reimbursements: {(error as any)?.message ?? String(error)}
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner size={18}/></div>
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState icon={Users} title="Nobody's out of pocket"
            description="Receipts coded as personal spend show up here until they've been billed to Xero." />
        </Card>
      ) : (
        <div className="space-y-2">
          {groups.map(g => {
            const open = openPerson === g.employee_id;
            const age  = ageTone(g.oldestDays);
            const unassigned = g.employee_id === "__unassigned__";
            return (
              <Card key={g.employee_id}>
                <button
                  type="button"
                  onClick={() => setOpenPerson(open ? null : g.employee_id)}
                  className="w-full text-left p-4 flex items-center gap-3"
                >
                  {open
                    ? <ChevronDown  size={16} className="text-text-muted flex-shrink-0" />
                    : <ChevronRight size={16} className="text-text-muted flex-shrink-0" />}

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[14px] font-medium text-text">{g.name}</span>
                      {unassigned ? (
                        <Pill label="needs a person" tone="warning" small />
                      ) : g.xero_contact_id ? (
                        <Pill label="Xero linked" tone="ok" small />
                      ) : (
                        <Pill label="not linked to Xero" tone="warning" small />
                      )}
                    </div>
                    <div className="text-[12px] text-text-muted mt-0.5">
                      {g.claims.length} {g.claims.length === 1 ? "receipt" : "receipts"}
                      {g.oldestDays != null && (
                        <> · <span className={age.cls}>oldest {g.oldestDays}d</span></>
                      )}
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <div className="text-[15px] font-semibold font-mono text-text">{fmtMoney(g.total)}</div>
                    <div className="text-[10px] uppercase tracking-wider2 text-text-muted">incl. GST</div>
                  </div>
                </button>

                {open && (
                  <div className="border-t border-line">
                    {unassigned && (
                      <div className="px-4 py-2.5 text-[12px] text-warn bg-warn/5 border-b border-line">
                        These were coded as personal spend but nobody was named, so there's no
                        one to pay back. Open each in the AP queue and set who it belongs to.
                      </div>
                    )}
                    <div className="divide-y divide-line">
                      {g.claims.map(c => {
                        const d = claimDate(c);
                        const a = ageDays(d);
                        return (
                          <label key={c.ap_invoice_id}
                                 className="px-4 py-2.5 flex items-start gap-3 cursor-pointer hover:bg-ink-raised">
                            {unassigned ? (
                              <Receipt size={14} className="text-text-muted mt-0.5 flex-shrink-0" />
                            ) : (
                              <input
                                type="checkbox"
                                checked={!excluded.has(c.ap_invoice_id)}
                                onChange={() => toggle(c.ap_invoice_id)}
                                disabled={busyPerson === g.employee_id}
                                className="mt-1 flex-shrink-0 accent-current"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] text-text truncate">
                                {c.supplier_name ?? c.description ?? "Receipt"}
                              </div>
                              <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1">
                                  <CalendarClock size={11}/>
                                  {d ? fmtDate(d) : "no date"}
                                  {a != null && <span className={ageTone(a).cls}> · {a}d</span>}
                                </span>
                                {c._proj      && <span>· {c._proj}</span>}
                                {c.budget_code && <span>· {c.budget_code}</span>}
                                {/* The account code is what G2 will settle. Showing it now
                                    makes the gaps obvious before the push exists. */}
                                <span>· {c.xero_account_code ? `acct ${c.xero_account_code}` : "no account code"}</span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-[13px] font-mono text-text">{fmtMoney(Number(c.amount_inc_gst ?? 0))}</div>
                              <div className="text-[10px] text-text-dim">
                                {fmtMoney(Number(c.amount_ex_gst ?? 0))} + {fmtMoney(Number(c.gst_amount ?? 0))} GST
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {/* What a bill would look like, stated plainly rather than
                        implied by a button that doesn't work yet. */}
                    {!unassigned && (() => {
                      const picked    = g.claims.filter(c => !excluded.has(c.ap_invoice_id));
                      const pickedSum = picked.reduce((a, c) => a + Number(c.amount_inc_gst ?? 0), 0);
                      const busy      = busyPerson === g.employee_id;
                      return (
                        <div className="px-4 py-3 border-t border-line bg-ink-raised flex items-center justify-between gap-4 flex-wrap">
                          <div className="text-[12px] text-text-muted min-w-0">
                            One bill to{" "}
                            <span className="text-text">{g.xero_contact_name ?? g.name}</span>{" "}
                            for <span className="text-text font-mono">{fmtMoney(pickedSum)}</span> across{" "}
                            {picked.length} {picked.length === 1 ? "line" : "lines"}.
                            {!g.xero_contact_id && (
                              <span className="text-warn"> Link them to a Xero contact first.</span>
                            )}
                          </div>
                          <Button
                            size="sm" icon={Upload}
                            onClick={() => pushBundle(g)}
                            disabled={busy || !g.xero_contact_id || picked.length === 0}
                          >
                            {busy ? "Sending…" : "Create one bill in Xero"}
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <div className="mt-5 text-[11px] text-text-dim">
        Bills are created as DRAFTS in Xero and still need approving there. Reimbursements
        no longer appear in Push to Xero — this is the only way they go across, so nobody
        can be paid twice.
      </div>
    </div>
  );
}
